// src/pages/EquipementDetailPage.tsx — SCE Issues
// Fiche détail d'un équipement du référentiel : identité + problèmes déclarés.
//
// ─── Rôle ───
//
// Complète EquipementListPage. Referme la boucle entre le référentiel et les
// déclarations : depuis un équipement, on voit tout son historique de
// problèmes (au lieu de devoir passer par /issues et filtrer à la main).
//
// ─── Accès ───
//
// Ouverte à tous les profils, comme la liste — voir l'en-tête de
// EquipementListPage pour la décision produit et ce qu'elle expose.

import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { AppShell } from '@/components/layout/AppShell'
import { Header } from '@/components/layout/Header'
import { useDansModale, useOuvrirFiche, type EtatModale } from '@/components/layout/ModaleRoute'
import { FNCT_MASQUEE } from '@/config/fnct.config'
import { accesFnct } from '@/utils/droitsFnct'
import { useBrouillonFnct } from '@/hooks/useBrouillonFnct'
import { cleBrouillonMachine, resumeBrouillon } from '@/utils/brouillonFnct'
import { Button } from '@/components/ui/Button'
import { LienFiche } from '@/components/ui/LienFiche'
import { lienChefProjet, lienProbleme, lienProjet } from '@/utils/liensFiches'
import { suiteDe } from '@/utils/suiteProblemes'
import { useIssues } from '@/hooks/useIssues'
import { getEquipementByNumeroSerie } from '@/api/equipements.api'
import { modeleParLibelle, nvsDeFourniture } from '@/config/equipements.catalogue'
import {
  CATEGORIES_PAR_SUFFIXE,
  cleRapprochement,
  suffixeNumeroSerie,
  type Equipement,
} from '@/types/issue.types'
import { Infobulle } from '@/components/ui/Infobulle'
import { libelleEDE } from '@/utils/documentationEDE'
import { useReferencesEDE } from '@/hooks/useReferencesEDE'
import { ExportMontage } from '@/components/equipements/ExportMontage'
import { CodeDesignation } from '@/components/equipements/CodeDesignation'
import { BadgeFabrication } from '@/components/equipements/BadgeFabrication'
import { useEnFabrication } from '@/hooks/useEnFabrication'
import { peutAttribuerMontages, useMontages } from '@/hooks/useMontages'
import { BadgeMontage } from '@/components/equipements/BadgeMontage'
import { rolesCharges } from '@/config/registreRoles'
import { logoDeSociete } from '@/utils/roles'
import { estSousTraite, METIERS_MONTAGE, montageDe, type MetierMontage } from '@/utils/montages'
import { separerNvs } from '@/utils/codeDesignation'
import { BoutonSuivreElement } from '@/components/issues/BoutonSuivreElement'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { ListeProblemesEquipement } from '@/components/equipements/ListeProblemesEquipement'

// FNC-T depuis la machine (15/10) : chargée à la demande, administrateur pendant la construction.
const FnctDepuisMachine = lazy(() => import('@/components/fnct/FnctDepuisMachine').then((m) => ({ default: m.FnctDepuisMachine })))

export function EquipementDetailPage(): JSX.Element {
  const navigate = useNavigate()
  const { numeroSerie } = useParams<{ numeroSerie: string }>()
  const dansModale = useDansModale()
  const ouvrirFiche = useOuvrirFiche()
  const { isFournisseur, user, enVueRole } = useCurrentUser()
  const { cles: clesFabrication } = useEnFabrication()
  // Montage sous-traité (29/10) : un badge par montage confié à une société ;
  // « à attribuer » pour les coordinateurs, qu'un clic mène à la page.
  const { montages, chargement: chargementMontages, erreur: erreurMontages } = useMontages()
  const attribueMontages = peutAttribuerMontages(user?.upn)
  const location = useLocation()
  // FNC-T sur cette machine (15/10, « machine d'abord ») : bouton de la fiche, ou arrivée
  // par « + › Une FNC-T » / « + Nouvelle FNC-T » (consigne retirée de l'historique ensuite).
  const fnctPermise = !isFournisseur && !FNCT_MASQUEE && accesFnct(user?.upn)
  const [fnctOuverte, setFnctOuverte] = useState(false)
  // Brouillon de FNC-T sur cet équipement (22/10) : le bouton dit « Reprendre ». Relu à la fermeture de la fenêtre.
  const [versionBrouillon, setVersionBrouillon] = useState(0)
  const brouillonFnct = useBrouillonFnct(
    fnctPermise && numeroSerie ? cleBrouillonMachine(numeroSerie) : '',
    enVueRole || !user ? '' : user.upn.toLowerCase(),
    versionBrouillon,
  )
  const ouvrirFnctALArrivee = (location.state as EtatModale | null)?.ouvrirFnct === true

  const [equipement, setEquipement] = useState<Equipement | null>(null)
  const [loading, setLoading] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)

  // ⚠️ `error` est récupéré, et affiché plus bas.
  //
  // Sans ça, un échec de chargement rendait EXACTEMENT le même écran qu'un
  // équipement sans problème : « Aucun problème déclaré sur cet équipement ».
  // C'est ce qui a masqué le rejet Graph pendant toute la phase de test — le
  // symptôme était indiscernable d'une machine saine. Même défaut, même
  // correction que dans ProjetDetailPage.
  //
  // Tous les problèmes sont lus (le filtre par N° de série se faisait déjà côté
  // client) : l'historique est celui de CET équipement, mais l'EDE se partage
  // entre équipements de même NVS (26/09).
  const {
    issues: tous,
    loading: loadingIssues,
    error: erreurIssues,
  } = useIssues()
  const issues = useMemo(() => {
    const cible = cleRapprochement(numeroSerie)
    return tous.filter((i) => cleRapprochement(i.numeroSerie) === cible)
  }, [tous, numeroSerie])
  const { refs: refsEDE, cle: cleEDE } = useReferencesEDE(tous)
  const referenceEDE = numeroSerie ? refsEDE.get(cleEDE(numeroSerie)) : undefined
  const nvsPartage = numeroSerie && cleEDE(numeroSerie).startsWith('NVS') ? cleEDE(numeroSerie) : null

  useEffect(() => {
    if (!numeroSerie) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setErreur(null)
    getEquipementByNumeroSerie(numeroSerie)
      .then((eq) => {
        if (!cancelled) setEquipement(eq)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setErreur(err instanceof Error ? err.message : 'Erreur de chargement')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [numeroSerie])

  useEffect(() => {
    if (!ouvrirFnctALArrivee || !equipement) return
    if (fnctPermise) setFnctOuverte(true)
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { ...(location.state as EtatModale), ouvrirFnct: undefined },
    })
  }, [ouvrirFnctALArrivee, equipement, fnctPermise, navigate, location.pathname, location.search, location.state])

  // En surimpression, la fiche ne doit pas s'envelopper dans AppShell : sinon
  // BottomNav en double, hauteur plein écran et en-tête collant redondant.
  // `Cadre` choisit l'enveloppe ; le corps de la page ne change pas.
  const Cadre = dansModale ? CadreModale : CadrePage
  const titre = numeroSerie ?? 'Équipement'

  if (loading) {
    return (
      <Cadre titre={titre} onBack={() => navigate('/equipements')}>
        <div className="p-4 flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg animate-pulse"
              style={{ height: 56, backgroundColor: 'var(--color-gray-10)' }}
            />
          ))}
        </div>
      </Cadre>
    )
  }

  if (erreur) {
    return (
      <Cadre titre={titre} onBack={() => navigate('/equipements')}>
        <div
          className="flex items-center gap-2 p-4 mt-4 mx-4 rounded"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-error)',
          }}
        >
          <AlertTriangle size={16} />
          <span style={{ fontSize: 'var(--font-size-copy2)' }}>{erreur}</span>
        </div>
      </Cadre>
    )
  }

  if (!equipement) {
    return (
      <Cadre titre={titre} onBack={() => navigate('/equipements')}>
        <div className="text-center py-10" style={{ color: 'var(--color-text-secondary)' }}>
          Équipement introuvable dans le référentiel.
        </div>
      </Cadre>
    )
  }

  const modele = modeleParLibelle(equipement.familleEquipement)
  // Confié à une société : pour tous les internes. À attribuer : pour qui attribue,
  // sur un équipement en fabrication, une fois les montages lus. En interne : rien.
  const enFabrication = clesFabrication.has(cleRapprochement(equipement.numeroSerie))
  const badgesMontage: { metier: MetierMontage; attribution: ReturnType<typeof montageDe> }[] =
    isFournisseur || chargementMontages || erreurMontages
      ? []
      : METIERS_MONTAGE.map((m) => ({ metier: m, attribution: montageDe(montages, equipement.numeroSerie, m) })).filter(
          (b) => estSousTraite(b.attribution) || (!b.attribution && attribueMontages && enFabrication),
        )
  const nvs = nvsDeFourniture(
    equipement.fourniture,
    CATEGORIES_PAR_SUFFIXE[suffixeNumeroSerie(equipement.numeroSerie)] ?? null,
  )
  // La désignation porte parfois le même NVS en fin (« … (NVS50365) ») : affiché
  // juste à côté, il n'est pas répété. Un NVS DIFFÉRENT reste visible.
  const separe = separerNvs(equipement.designation ?? '')
  const designation = nvs && separe.nvs === nvs ? separe.code.trim() : equipement.designation

  return (
    <Cadre titre={titre} onBack={() => navigate('/equipements')}>
      <div className="p-4 flex flex-col gap-4">
        {/* Identité */}
        <section
          style={{
            backgroundColor: 'var(--color-white)',
            borderRadius: 12,
            padding: 16,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          }}
        >
          {/* Lot 12 ter : suivre l'équipement. Cloche seule, au même endroit que sur
              la fiche projet. Pas pour un fournisseur (Lot 12). */}
          {!isFournisseur && (
            <div className="flex justify-end" style={{ margin: '-6px -6px 0' }}>
              <BoutonSuivreElement genre="equipement" id={equipement.numeroSerie} />
            </div>
          )}
          <h2
            style={{
              fontSize: 'var(--font-size-h2)',
              lineHeight: 'var(--line-height-h2)',
              fontWeight: 500,
              margin: '0 0 4px',
            }}
          >
            {equipement.familleEquipement || 'Modèle non renseigné'}
          </h2>
          {/* Désignation | NVS (05/10) : le NVS est lu dans la fourniture Windchill,
              qui n'a plus de ligne à elle et s'affiche au survol du NVS. */}
          {(designation || nvs) && (
            <p
              className="flex items-center flex-wrap"
              style={{
                fontSize: 'var(--font-size-copy2)',
                color: 'var(--color-text-secondary)',
                margin: '0 0 10px',
                columnGap: 10,
              }}
            >
              {designation && <CodeDesignation code={designation} />}
              {designation && nvs && (
                <span
                  aria-hidden="true"
                  style={{ width: 1, alignSelf: 'stretch', backgroundColor: 'var(--color-gray-20)' }}
                />
              )}
              {nvs &&
                (equipement.fourniture ? (
                  <Infobulle titre={equipement.fourniture} detail="Fourniture (Windchill)" largeur={320}>
                    <span tabIndex={0} style={{ cursor: 'help' }}>{nvs}</span>
                  </Infobulle>
                ) : (
                  <span>{nvs}</span>
                ))}
            </p>
          )}

          {/* « En fabrication » (05/10) : dossier de production en cours, à côté de la gamme et du type. */}
          {(modele || clesFabrication.has(cleRapprochement(equipement.numeroSerie)) || badgesMontage.length > 0) && (
            <div className="flex gap-2 flex-wrap items-center" style={{ marginBottom: 10 }}>
              {clesFabrication.has(cleRapprochement(equipement.numeroSerie)) && <BadgeFabrication />}
              {badgesMontage.map((b) => (
                <BadgeMontage
                  key={b.metier}
                  metier={b.metier}
                  attribution={b.attribution}
                  logo={b.attribution ? logoDeSociete(b.attribution.valeur, rolesCharges()?.societes ?? []) : null}
                  onClick={attribueMontages ? () => navigate('/montages', { state: { recherche: equipement.numeroSerie } }) : undefined}
                />
              ))}
              {modele && <Tag>{modele.gamme}</Tag>}
              {modele && <Tag>{modele.type}</Tag>}
            </div>
          )}

          <div style={{ borderTop: '1px solid var(--color-gray-10)', marginTop: 8 }}>
            <InfoRow label="N° de série" value={equipement.numeroSerie} />
            <InfoRow label="OF" value={equipement.of || '—'} />
            <InfoRow
              label="PN"
              value={equipement.pn || '—'}
              lien={equipement.pn ? lienProjet(equipement.pn) : undefined}
              libelleLien={`Voir le projet ${equipement.pn}`}
            />
            {equipement.cve && <InfoRow label="CVE" value={equipement.cve} />}
            {equipement.chefProjet && (
              <InfoRow
                label="Chef de projet"
                value={equipement.chefProjet}
                lien={lienChefProjet(equipement.chefProjet)}
                libelleLien={`Voir les projets de ${equipement.chefProjet}`}
              />
            )}
            {/* Client du projet (Customer Name de Windchill, 20/10) : discret, pour se repérer. */}
            {equipement.client && <InfoRow label="Client" value={equipement.client} discret />}
            {/* Sans NVS lisible, la fourniture garde sa ligne : rien d'autre ne la montre. */}
            {equipement.fourniture && !nvs && (
              <InfoRow label="Fourniture (Windchill)" value={equipement.fourniture} italicGris />
            )}
            {/* Schéma électrique (Lot 13) : le plus récent renseigné sur les
                problèmes de cette machine — absent de Windchill. */}
            {referenceEDE && (
              <InfoRow
                label="EDE"
                value={
                  libelleEDE(referenceEDE.numero, referenceEDE.rev) +
                  (nvsPartage ? ` · commun aux équipements ${nvsPartage}` : '')
                }
              />
            )}
          </div>
        </section>

        {/* L'équipement est passé en paramètre d'URL : le bouton promet une
            déclaration sur CETTE machine, il ne doit pas obliger à retaper le
            numéro de série affiché juste au-dessus. */}
        <Button
          variant="primary"
          onClick={() =>
            navigate(
              `/new?equipement=${encodeURIComponent(equipement.numeroSerie)}`,
            )
          }
          fullWidth
        >
          Déclarer un problème
        </Button>
        {fnctPermise && (
          <div>
            <Button variant="secondary" onClick={() => setFnctOuverte(true)} fullWidth>
              {brouillonFnct ? 'Reprendre la FNC-T en cours' : 'Ouvrir une FNC-T'}
            </Button>
            {brouillonFnct && (
              <p style={{ margin: '6px 0 0', textAlign: 'center', fontSize: 13, lineHeight: '18px', color: 'var(--color-gray-70)' }}>
                {resumeBrouillon(brouillonFnct)}
              </p>
            )}
          </div>
        )}
        {fnctOuverte && (
          <Suspense fallback={null}>
            <FnctDepuisMachine
              equipement={equipement}
              problemes={issues}
              onClose={() => {
                setFnctOuverte(false)
                setVersionBrouillon((v) => v + 1)
              }}
            />
          </Suspense>
        )}

        {/* Seul export Excel de l'appli (27/09) : la feuille « Montage et
            Tests » de l'usine, ELEC / AUTOM ou MECA. Pas pour un fournisseur :
            elle porte CVE, PN et chef de projet. */}
        {!isFournisseur && (
          <ExportMontage
            equipement={equipement}
            issues={issues}
            refEDE={referenceEDE}
            chargement={loadingIssues || erreurIssues !== null}
          />
        )}

        {/* Problèmes déclarés */}
        <section>
          <h3
            style={{
              fontSize: 'var(--font-size-copy2)',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              margin: '4px 0 10px',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            Problèmes déclarés {issues.length > 0 && `(${issues.length})`}
          </h3>

          {loadingIssues && issues.length === 0 && (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg animate-pulse"
                  style={{ height: 48, backgroundColor: 'var(--color-gray-10)' }}
                />
              ))}
            </div>
          )}

          {/* Un échec de chargement ne doit PAS se déguiser en « aucun
              problème » : sans ce bandeau, la panne et l'absence de données
              rendaient le même écran. */}
          {erreurIssues && (
            <div
              className="flex items-start gap-2 p-3 rounded"
              style={{
                backgroundColor: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-error)',
                fontSize: 'var(--font-size-copy2)',
              }}
            >
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{erreurIssues}</span>
            </div>
          )}

          {!loadingIssues && !erreurIssues && issues.length === 0 && (
            <div
              className="text-center py-6 rounded"
              style={{ backgroundColor: 'var(--color-white)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-copy2)' }}
            >
              Aucun problème déclaré sur cet équipement.
            </div>
          )}

          {/* Filtres, à traiter puis terminés, aperçu en volet sur PC (10/10). Depuis la
              modale équipement, la fiche complète s'ouvre elle aussi en modale,
              sur le même fond ; « Problème suivant » parcourt la liste affichée. */}
          {issues.length > 0 && (
            <ListeProblemesEquipement
              issues={issues}
              onOuvrirFiche={(issue, suite) =>
                ouvrirFiche(lienProbleme(issue.id), suiteDe(`Problèmes de ${numeroSerie ?? ''}`, suite))
              }
            />
          )}
        </section>
      </div>
    </Cadre>
  )
}

// ─── Cadres : page pleine ou surimpression ───

interface CadreProps {
  titre: string
  onBack: () => void
  children: React.ReactNode
}

/** Rendu classique : header collant + AppShell + BottomNav. */
function CadrePage({ titre, onBack, children }: CadreProps): JSX.Element {
  return (
    <AppShell header={<Header level={3} title={titre} showBack onBack={onBack} />}>
      {children}
    </AppShell>
  )
}

/**
 * Rendu en surimpression : pas d'AppShell, pas de BottomNav, pas de header
 * collant. Un simple bandeau de titre, la croix de fermeture étant fournie
 * par ModaleRoute.
 */
function CadreModale({ titre, children }: CadreProps): JSX.Element {
  return (
    <div>
      <div
        style={{
          padding: '18px 16px 0',
          fontSize: 'var(--font-size-copy1)',
          lineHeight: 'var(--line-height-copy1)',
          fontWeight: 500,
        }}
      >
        {titre}
      </div>
      {children}
    </div>
  )
}

// ─── Sous-composants ───

function Tag({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span
      style={{
        fontSize: 'var(--font-size-info)',
        color: 'var(--color-text-secondary)',
        backgroundColor: 'var(--color-gray-5)',
        borderRadius: 6,
        padding: '3px 8px',
      }}
    >
      {children}
    </span>
  )
}

function InfoRow({
  label,
  value,
  italicGris,
  discret,
  lien,
  libelleLien,
}: {
  label: string
  value: string
  italicGris?: boolean
  /** Information de contexte (le client) : texte secondaire. */
  discret?: boolean
  /** Fiche à ouvrir au clic sur la valeur (PN, chef de projet). */
  lien?: string
  libelleLien?: string
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}>
        {label}
      </span>
      <span
        className="text-right max-w-[60%]"
        style={{
          fontSize: 'var(--font-size-copy2)',
          color: italicGris ? 'var(--color-gray-50)' : discret ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
          fontStyle: italicGris ? 'italic' : 'normal',
          overflowWrap: 'anywhere',
        }}
      >
        {lien ? (
          <LienFiche vers={lien} titre={libelleLien}>
            {value}
          </LienFiche>
        ) : (
          value
        )}
      </span>
    </div>
  )
}
